/**
 * ProviderPortalPages - Telas mobile-first do Portal do Prestador (/prestador/:tenantSlug).
 *
 * Fase 1: entrar, ativar acesso (criar senha) e Início com os dados da sessão.
 * Validação real sempre na Edge Function `provider-portal`.
 *
 * RELEASE: 2026-10-12 v1.2.0 - Entrada alinhada à moldura premium do aplicativo.
 * RELEASE: 2026-10-12 v1.1.0 - Rotas em /prestador; Início movido para ProviderPortalApp.
 * RELEASE: 2026-10-12 v1.0.0 - Versão inicial.
 */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Loader2, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { callProviderPortal, providerPortalClient } from '@/lib/providerPortal';
import { ProviderSystemBrand } from './ProviderSystemBrand';
import { useProviderPortalPresentation } from './useProviderPortalPresentation';

interface SessionMe { provider: { name: string; email: string }; tenant: { name: string; logo_url: string | null } }

/** Moldura de tela cheia no celular, centralizada no computador. */
function Shell({ children }: { children: ReactNode }) {
  return <div className="provider-app min-h-[100dvh] border-t-8 border-sidebar bg-background text-foreground"><div className="mx-auto flex min-h-[100dvh] max-w-md flex-col justify-center px-6 py-10">{children}</div></div>;
}

function Brand({ title, subtitle }: { title: string; subtitle: string }) {
  const { tenantSlug = '' } = useParams();
  const presentation = useProviderPortalPresentation(tenantSlug);
  // Atalho criado já na tela de entrada também abre o app do prestador.
  return (
    <div className="mb-8 flex flex-col items-start gap-3">
      <div className="mb-4 w-full"><ProviderSystemBrand login /></div>
      <h1 className="text-2xl font-bold">{title}</h1>
      <p className="text-sm text-muted-foreground">{subtitle}</p>
      <div className="mt-2 flex min-h-12 w-full items-center gap-3 border-y border-border py-3">
        <Building2 className="h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0"><p className="text-xs text-muted-foreground">Empresa</p>
          {presentation.isLoading ? <span className="text-sm text-muted-foreground">Carregando…</span>
            : presentation.data ? <p className="break-words text-sm font-semibold">{presentation.data.tenant.name}</p>
              : <p role="alert" className="text-sm text-destructive">{(presentation.error as Error)?.message ?? 'Portal indisponível'}</p>}
        </div>
      </div>
    </div>
  );
}


export function ProviderPortalLogin() {
  const { tenantSlug = '' } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    const { error: err } = await providerPortalClient.auth.signInWithPassword({ email: email.trim(), password });
    if (err) { setBusy(false); setError('E-mail ou senha inválidos'); return; }
    try {
      await callProviderPortal(tenantSlug, 'session.me'); // confirma o vínculo com este tenant
      navigate(`/prestador/${tenantSlug}`, { replace: true });
    } catch (x) {
      await providerPortalClient.auth.signOut();
      setError((x as Error).message);
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      <Brand title="Portal do Prestador" subtitle="Entre para ver suas ordens de serviço" />
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5"><Label htmlFor="email">E-mail</Label>
          <Input id="email" type="email" inputMode="email" autoComplete="email" maxLength={255} className="h-12" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
        <div className="space-y-1.5"><Label htmlFor="pwd">Senha</Label>
          <Input id="pwd" type="password" autoComplete="current-password" maxLength={72} className="h-12" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
        {error && <p className="text-sm font-medium text-destructive">{error}</p>}
        <Button type="submit" className="h-12 w-full" disabled={busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Entrar</Button>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground">Primeiro acesso? <Link className="text-primary" to={`/prestador/${tenantSlug}/ativar`}>Ativar acesso</Link></p>
    </Shell>
  );
}

export function ProviderPortalActivate() {
  const { tenantSlug = '' } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', document: '', password: '', confirm: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (form.password.length < 8) return setError('A senha precisa ter no mínimo 8 caracteres');
    if (form.password !== form.confirm) return setError('As senhas não conferem');
    setBusy(true); setError(null);
    try {
      await callProviderPortal(tenantSlug, 'auth.activate', {
        email: form.email.trim(), document: form.document.replace(/\D/g, ''), password: form.password,
      });
      await providerPortalClient.auth.signInWithPassword({ email: form.email.trim(), password: form.password });
      navigate(`/prestador/${tenantSlug}`, { replace: true });
    } catch (x) { setError((x as Error).message); } finally { setBusy(false); }
  };

  return (
    <Shell>
      <Brand title="Ativar acesso" subtitle="Use o e-mail do convite e o CPF/CNPJ do seu cadastro" />
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5"><Label>E-mail do convite</Label><Input type="email" className="h-12" maxLength={255} value={form.email} onChange={set('email')} required /></div>
        <div className="space-y-1.5"><Label>CPF ou CNPJ</Label><Input inputMode="numeric" className="h-12" maxLength={18} value={form.document} onChange={set('document')} required /></div>
        <div className="space-y-1.5"><Label>Crie uma senha</Label><Input type="password" autoComplete="new-password" className="h-12" maxLength={72} value={form.password} onChange={set('password')} required /></div>
        <div className="space-y-1.5"><Label>Repita a senha</Label><Input type="password" autoComplete="new-password" className="h-12" maxLength={72} value={form.confirm} onChange={set('confirm')} required /></div>
        {error && <p className="text-sm font-medium text-destructive">{error}</p>}
        <Button type="submit" className="h-12 w-full" disabled={busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Ativar e entrar</Button>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground"><Link className="text-primary" to={`/prestador/${tenantSlug}/login`}>Já tenho acesso</Link></p>
    </Shell>
  );
}

// Fase 2: Início, OS e Estoque vivem em ProviderPortalApp.tsx
export { ProviderPortalHome } from './ProviderPortalApp';
